// TMOS Business Position Service
// Date: December 19, 2025
// Purpose: Frontend service for business positions and position-role mappings

import api from './api';

export interface BusinessPosition {
  id: string;
  position_code: string;
  position_name: string;
  position_category?: string;
  description?: string;
  is_system_position: boolean;
  is_active: boolean;
}

export interface PositionRoleMapping {
  id: string;
  position_id: string;
  role_code: string;
  tenant_id?: string;
  is_default: boolean;
  priority: number;
}

export interface UserPosition {
  id: string;
  user_id: string;
  position_id: string;
  tenant_id: string;
  hierarchy_location?: {
    geo_id?: string;
    func_id?: string;
    bus_id?: string;
  };
  assigned_role?: string;
  is_primary: boolean;
  assigned_at: string;
  assigned_by?: string;
}

export const businessPositionService = {
  // Get all active positions
  async getActivePositions(category?: string): Promise<BusinessPosition[]> {
    const params = category ? { category } : {};
    const response = await api.get('/business-positions/positions', { params });
    return response.data.positions || [];
  },

  // Get positions by category
  async getPositionsByCategory(category: string): Promise<BusinessPosition[]> {
    const response = await api.get(`/business-positions/positions/category/${category}`);
    return response.data.positions || [];
  },

  // Get roles for a position
  async getRolesForPosition(positionId: string): Promise<string[]> {
    const response = await api.get(`/business-positions/position/${positionId}/roles`);
    return response.data.roles || [];
  },

  // Get primary role for a position
  async getPrimaryRoleForPosition(positionId: string): Promise<string | null> {
    const response = await api.get(`/business-positions/position/${positionId}/primary-role`);
    return response.data.role || null;
  },

  // Get user positions
  async getUserPositions(): Promise<UserPosition[]> {
    const response = await api.get('/business-positions/user/positions');
    return response.data.positions || [];
  },

  // Get user primary position
  async getUserPrimaryPosition(): Promise<UserPosition | null> {
    const response = await api.get('/business-positions/user/primary-position');
    return response.data.position || null;
  },

  // Assign position to user
  async assignPositionToUser(
    positionId: string,
    hierarchyLocation?: { geo_id?: string; func_id?: string; bus_id?: string }
  ): Promise<{ userPosition: UserPosition; assignedRole: string | null }> {
    const response = await api.post('/business-positions/user/assign-position', {
      positionId,
      hierarchyLocation
    });
    return {
      userPosition: response.data.userPosition,
      assignedRole: response.data.assignedRole
    };
  },

  // Set primary position for user
  async setUserPrimaryPosition(positionId: string): Promise<UserPosition> {
    const response = await api.post('/business-positions/user/set-primary-position', { positionId });
    return response.data.position;
  },

  // Create or update position-role mapping
  async upsertPositionRoleMapping(
    positionId: string,
    roleCode: string,
    priority?: number
  ): Promise<PositionRoleMapping> {
    const response = await api.post('/business-positions/mapping', {
      positionId,
      roleCode,
      priority
    });
    return response.data.mapping;
  },

  // --- Platform admin: position templates ---
  async getPlatformTemplates(category?: string): Promise<BusinessPosition[]> {
    const params = category ? { category } : {};
    const response = await api.get('/business-positions/platform/templates', { params });
    return response.data.positions || [];
  },

  async createPlatformTemplate(data: {
    position_code: string;
    position_name: string;
    position_category?: string;
    description?: string;
    is_system_position?: boolean;
    is_active?: boolean;
  }): Promise<BusinessPosition> {
    const response = await api.post('/business-positions/platform/templates', data);
    return response.data.position;
  },

  async updatePlatformTemplate(
    positionId: string,
    data: { position_name?: string; position_category?: string; description?: string; is_active?: boolean }
  ): Promise<BusinessPosition> {
    const response = await api.put(`/business-positions/platform/templates/${positionId}`, data);
    return response.data.position;
  },

  async deletePlatformTemplate(positionId: string): Promise<void> {
    await api.delete(`/business-positions/platform/templates/${positionId}`);
  },

  async provisionPositionsToTenant(tenantId: string, positionIds: string[]): Promise<BusinessPosition[]> {
    const response = await api.post('/business-positions/platform/provision-to-tenant', {
      tenantId,
      positionIds
    });
    return response.data.provisioned || [];
  },

  // --- Tenant admin: tenant-scoped positions ---
  async createTenantPosition(data: {
    position_code: string;
    position_name: string;
    position_category?: string;
    description?: string;
    is_system_position?: boolean;
    is_active?: boolean;
  }): Promise<BusinessPosition> {
    const response = await api.post('/business-positions/tenant/positions', data);
    return response.data.position;
  },

  async updateTenantPosition(
    positionId: string,
    data: { position_name?: string; position_category?: string; description?: string; is_active?: boolean }
  ): Promise<BusinessPosition> {
    const response = await api.put(`/business-positions/tenant/positions/${positionId}`, data);
    return response.data.position;
  },

  async deleteTenantPosition(positionId: string): Promise<void> {
    await api.delete(`/business-positions/tenant/positions/${positionId}`);
  }
};

